// usePlaybookSteps — fetch an AI-generated playbook (ordered steps with
// title / body / prompt) for any in-progress task surface. Wraps the
// `task-suggestions` edge function in `mode: "playbook"` so the same
// hook serves QuestWorkshop, HackerGuide, and the in-chat checklist.
//
// Depth is render-aware: switching short/detailed/expert refetches so
// the AI can tune step count + verbosity. We dedupe in-flight requests
// per (kind, title, depth) and cache the last successful payload per
// key so depth-toggling feels instant on repeat hits.
import { useEffect, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import type { TaskKind } from "@/hooks/useTaskSuggestions";
import type { PlaybookDepth } from "@/lib/playbookDepth";

export interface PlaybookStep {
  title: string;
  body: string;
  prompt: string;
}

interface Args {
  kind: TaskKind;
  title: string;
  description?: string;
  context?: string;
  depth: PlaybookDepth;
  /** Skip the network call (e.g. checklist collapsed). */
  enabled?: boolean;
}

const memoryCache = new Map<string, PlaybookStep[]>();
const cacheKey = (a: Args) => `${a.kind}::${a.title}::${a.depth}`;

export const usePlaybookSteps = ({
  kind,
  title,
  description,
  context,
  depth,
  enabled = true,
}: Args) => {
  const { user } = useAuth();
  const [steps, setSteps] = useState<PlaybookStep[]>(() =>
    memoryCache.get(cacheKey({ kind, title, description, context, depth })) ?? [],
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const reqId = useRef(0);

  useEffect(() => {
    if (!enabled || !user || !title) {
      setError(null);
      return;
    }
    const key = cacheKey({ kind, title, description, context, depth });
    const cached = memoryCache.get(key);
    if (cached) {
      setSteps(cached);
      // Still refresh in the background? No — depth-toggling should
      // feel free. Users can hit "regenerate" to force a refetch.
      return;
    }
    const myId = ++reqId.current;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const { data, error: fnErr } = await supabase.functions.invoke(
          "task-suggestions",
          {
            body: { kind, title, description, context, mode: "playbook", depth },
          },
        );
        if (myId !== reqId.current) return;
        if (fnErr) {
          setError(fnErr.message ?? "Could not load playbook");
          setSteps([]);
        } else {
          const next = Array.isArray(data?.steps) ? (data.steps as PlaybookStep[]) : [];
          memoryCache.set(key, next);
          setSteps(next);
        }
      } catch (e) {
        if (myId !== reqId.current) return;
        setError(e instanceof Error ? e.message : "Could not load playbook");
      } finally {
        if (myId === reqId.current) setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, user?.id, kind, title, depth]);

  /** Force a refetch (bypasses the in-memory cache). */
  const regenerate = async () => {
    if (!user || !title) return;
    const key = cacheKey({ kind, title, description, context, depth });
    memoryCache.delete(key);
    const myId = ++reqId.current;
    setLoading(true);
    setError(null);
    try {
      const { data, error: fnErr } = await supabase.functions.invoke(
        "task-suggestions",
        { body: { kind, title, description, context, mode: "playbook", depth } },
      );
      if (myId !== reqId.current) return;
      if (fnErr) {
        setError(fnErr.message ?? "Could not load playbook");
        setSteps([]);
      } else {
        const next = Array.isArray(data?.steps) ? (data.steps as PlaybookStep[]) : [];
        memoryCache.set(key, next);
        setSteps(next);
      }
    } catch (e) {
      if (myId !== reqId.current) return;
      setError(e instanceof Error ? e.message : "Could not load playbook");
    } finally {
      if (myId === reqId.current) setLoading(false);
    }
  };

  return { steps, loading, error, regenerate };
};